import { bindable, customElement, observable } from 'aurelia';
import template from './advanced-person-filter-modal.html';
import type {
  AdvancedPersonFilter,
  AdvancedPersonFilterComparison,
  FaceMarkerSize,
  FaceType,
  Person,
} from '../services/api.types';
import {
  FaceMarkerSize as FaceMarkerSizeValue,
} from '../services/api.types';
import { formatPersonName } from '../services/api.service';
import { FACE_TYPE_OPTIONS } from '../services/face-type-options';

interface PersonFilterDraftCondition {
  id: number;
  person: Person | null;
  negated: boolean;
  faceTypes: FaceType[];
  faceSize: FaceMarkerSize | null;
  faceSizeComparison: AdvancedPersonFilterComparison | null;
  generalRating: number | null;
  generalRatingComparison: AdvancedPersonFilterComparison | null;
}

@customElement({ name: 'advanced-person-filter-modal', template })
export class AdvancedPersonFilterModal {
  @bindable public filter: AdvancedPersonFilter | null = null;
  @bindable public applied!: (filter: AdvancedPersonFilter | null) => void;
  @bindable public closed!: () => void;

  public operator: 'and' | 'or' = 'and';
  public conditions: PersonFilterDraftCondition[] = [];
  public personPickerCondition: PersonFilterDraftCondition | null = null;
  public faceTypeMenuConditionId: number | null = null;
  @observable public faceTypeMenuStyle = '';
  private _nextConditionId = 1;
  private _keyHandler: ((event: KeyboardEvent) => void) | null = null;
  private _documentClickHandler: ((event: MouseEvent) => void) | null = null;
  private readonly _faceTypePositionHandler = (): void => this.positionFaceTypeMenu();

  public readonly faceTypeOptions = FACE_TYPE_OPTIONS;

  public readonly markerSizeOptions: Array<{ value: FaceMarkerSize; labelKey: string }> = [
    { value: FaceMarkerSizeValue.Micro, labelKey: 'faceRatings.sizes.micro' },
    { value: FaceMarkerSizeValue.Mini, labelKey: 'faceRatings.sizes.mini' },
    { value: FaceMarkerSizeValue.Small, labelKey: 'faceRatings.sizes.small' },
    { value: FaceMarkerSizeValue.Medium, labelKey: 'faceRatings.sizes.medium' },
    { value: FaceMarkerSizeValue.Large, labelKey: 'faceRatings.sizes.large' },
    { value: FaceMarkerSizeValue.Huge, labelKey: 'faceRatings.sizes.huge' },
  ];

  public readonly comparisonOptions: Array<{
    value: AdvancedPersonFilterComparison;
    labelKey: string;
  }> = [
    { value: 'atLeast', labelKey: 'gallery.advancedPersonFilter.comparison.atLeast' },
    { value: 'atMost', labelKey: 'gallery.advancedPersonFilter.comparison.atMost' },
    { value: 'exact', labelKey: 'gallery.advancedPersonFilter.comparison.exact' },
  ];

  public readonly ratingOptions = [1, 2, 3, 4, 5];

  public attached(): void {
    this.resetDraft();
    this._keyHandler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (this.faceTypeMenuConditionId !== null) {
        event.preventDefault();
        event.stopPropagation();
        this.faceTypeMenuConditionId = null;
      } else if (this.personPickerCondition) {
        event.preventDefault();
        event.stopPropagation();
        this.closePersonPicker();
      } else {
        this.close();
      }
    };
    this._documentClickHandler = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element
        && target.closest('.advanced-person-filter__face-type, .advanced-person-filter__face-type-menu')) return;
      this.faceTypeMenuConditionId = null;
    };
    document.addEventListener('keydown', this._keyHandler);
    document.addEventListener('click', this._documentClickHandler);
    window.addEventListener('scroll', this._faceTypePositionHandler, true);
    window.addEventListener('resize', this._faceTypePositionHandler);
  }

  public detached(): void {
    if (this._keyHandler) document.removeEventListener('keydown', this._keyHandler);
    if (this._documentClickHandler) document.removeEventListener('click', this._documentClickHandler);
    window.removeEventListener('scroll', this._faceTypePositionHandler, true);
    window.removeEventListener('resize', this._faceTypePositionHandler);
    this._keyHandler = null;
    this._documentClickHandler = null;
  }

  public resetDraft(): void {
    this.operator = this.filter?.operator ?? 'and';
    this._nextConditionId = 1;
    this.personPickerCondition = null;
    this.faceTypeMenuConditionId = null;
    this.faceTypeMenuStyle = '';
    this.conditions = this.filter?.conditions.map(condition => ({
      id: this._nextConditionId++,
      person: condition.person,
      negated: condition.negated,
      faceTypes: condition.faceTypes?.slice()
        ?? (condition.faceType == null ? [] : [condition.faceType]),
      faceSize: condition.faceSize ?? condition.minimumSize ?? null,
      faceSizeComparison: condition.faceSizeComparison
        ?? ((condition.faceSize ?? condition.minimumSize) != null ? 'atLeast' : null),
      generalRating: condition.generalRating ?? condition.minimumGeneralRating ?? null,
      generalRatingComparison: condition.generalRatingComparison
        ?? ((condition.generalRating ?? condition.minimumGeneralRating) != null ? 'atLeast' : null),
    })) ?? [];
    if (this.conditions.length === 0) this.addCondition();
  }

  public addCondition(): void {
    this.conditions = [
      ...this.conditions,
      {
        id: this._nextConditionId++,
        person: null,
        negated: false,
        faceTypes: [],
        faceSize: null,
        faceSizeComparison: null,
        generalRating: null,
        generalRatingComparison: null,
      },
    ];
  }

  public removeCondition(condition: PersonFilterDraftCondition): void {
    this.conditions = this.conditions.filter(item => item.id !== condition.id);
    if (this.faceTypeMenuConditionId === condition.id) this.faceTypeMenuConditionId = null;
  }

  public isConditionValid(condition: PersonFilterDraftCondition): boolean {
    return condition.person !== null
      && (!condition.faceSizeComparison || condition.faceSize != null)
      && (!condition.generalRatingComparison || condition.generalRating != null);
  }

  public hasValidPerson(condition: PersonFilterDraftCondition): boolean {
    return condition.person !== null;
  }

  public hasValidConstraints(condition: PersonFilterDraftCondition): boolean {
    return (!condition.faceSizeComparison || condition.faceSize != null)
      && (!condition.generalRatingComparison || condition.generalRating != null);
  }

  public get canApply(): boolean {
    return this.conditions.every(condition => this.isConditionValid(condition));
  }

  public close(): void {
    this.closed();
  }

  public toggleFaceTypeMenu(condition: PersonFilterDraftCondition, event: MouseEvent): void {
    event.stopPropagation();
    this.faceTypeMenuConditionId = this.faceTypeMenuConditionId === condition.id
      ? null
      : condition.id;
    if (this.faceTypeMenuConditionId !== null) {
      requestAnimationFrame(() => this.positionFaceTypeMenu());
    }
  }

  public toggleFaceType(condition: PersonFilterDraftCondition, faceType: FaceType): void {
    condition.faceTypes = condition.faceTypes.includes(faceType)
      ? condition.faceTypes.filter(value => value !== faceType)
      : [...condition.faceTypes, faceType];
  }

  public get faceTypeMenuCondition(): PersonFilterDraftCondition | null {
    return this.conditions.find(condition => condition.id === this.faceTypeMenuConditionId) ?? null;
  }

  private positionFaceTypeMenu(): void {
    if (this.faceTypeMenuConditionId === null) return;
    const button = document.querySelector<HTMLElement>(
      `[data-advanced-face-type-id="${this.faceTypeMenuConditionId}"]`,
    );
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewportPadding = 8;
    const estimatedHeight = this.faceTypeOptions.length * 34 + 16;
    const openAbove = rect.bottom + estimatedHeight > window.innerHeight - viewportPadding
      && rect.top - estimatedHeight >= viewportPadding;
    const top = openAbove
      ? rect.top - estimatedHeight - 4
      : rect.bottom + 4;
    const width = Math.min(
      Math.max(rect.width, 240),
      Math.max(160, window.innerWidth - viewportPadding * 2),
    );
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
    );

    this.faceTypeMenuStyle = [
      `top: ${Math.max(viewportPadding, top)}px`,
      `left: ${left}px`,
      `min-width: ${width}px`,
    ].join('; ');
  }

  public onFaceSizeComparisonChanged(condition: PersonFilterDraftCondition): void {
    if (!condition.faceSizeComparison) condition.faceSize = null;
  }

  public onGeneralRatingComparisonChanged(condition: PersonFilterDraftCondition): void {
    if (!condition.generalRatingComparison) condition.generalRating = null;
  }

  public personLabel(person: Person | null): string {
    return person ? formatPersonName(person) : '';
  }

  public openPersonPicker(condition: PersonFilterDraftCondition): void {
    this.personPickerCondition = condition;
  }

  public onPersonPicked(person: Person): void {
    const condition = this.personPickerCondition;
    if (!condition) return;
    condition.person = person;
    this.personPickerCondition = null;
  }

  public closePersonPicker(): void {
    this.personPickerCondition = null;
  }

  public clear(): void {
    this.applied(null);
  }

  public apply(): void {
    if (!this.canApply) return;
    if (this.conditions.length === 0) {
      this.applied(null);
      return;
    }

    this.applied({
      operator: this.operator,
      conditions: this.conditions.map(condition => ({
        personId: condition.person!.id,
        person: condition.person!,
        negated: condition.negated,
        faceTypes: condition.faceTypes.length > 0 ? [...condition.faceTypes] : null,
        faceType: condition.faceTypes.length === 1 ? condition.faceTypes[0] : null,
        faceSize: condition.faceSizeComparison ? condition.faceSize : null,
        faceSizeComparison: condition.faceSizeComparison,
        generalRating: condition.generalRatingComparison ? condition.generalRating : null,
        generalRatingComparison: condition.generalRatingComparison,
      })),
    });
  }
}
